import React, { useEffect, useRef, useState } from 'react'
import './picker.css'

// Trigger text: first two names + ' +N'; 'All' when multi and everything is selected; emptyText when empty.
function summaryText(options, selected, multi, emptyText) {
    const texts = new Map(options.map(option => [option.value, option.text]));
    const names = selected.map(value => texts.get(value));
    let summary = names.length > 2 ? names.slice(0, 2).join(', ') + ' +' + (names.length - 2) : names.join(', ');
    if (!names.length) summary = emptyText;
    else if (multi && names.length === options.length) summary = 'All';
    return { summary, names };
}

/**
 * Searchable single/multi select — React port of benchmark-mock/render/picker.js.
 * Controlled: the parent owns `selected` and receives the new values via onChange(values).
 * emptyText: trigger text when nothing is selected (default 'No <label>').
 */
export default function Picker({ label, options, selected, multi, onChange, emptyText = 'No ' + label.toLowerCase() }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const root = useRef(null);
    const trigger = useRef(null);
    const search = useRef(null);

    const close = (restoreFocus) => {
        setOpen(false);
        if (restoreFocus) trigger.current.focus();
    };

    useEffect(() => {
        if (!open) return undefined;
        search.current.focus();
        const onClick = event => {
            if (!root.current.contains(event.target)) setOpen(false);
        };
        const onKey = event => {
            if (event.key !== 'Escape') return;
            event.preventDefault();
            setOpen(false);
            trigger.current.focus();
        };
        document.addEventListener('click', onClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('click', onClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const toggle = () => {
        if (open) return close();
        setQuery('');
        setOpen(true);
    };

    const pick = (value) => {
        if (multi) {
            onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);
        } else {
            close(true);
            onChange([value]);
        }
    };

    const { summary, names } = summaryText(options, selected, multi, emptyText);
    const placeholder = 'Search ' + label.toLowerCase();
    const needle = query.toLocaleLowerCase();
    const visible = option => option.text.toLocaleLowerCase().includes(needle);
    // Select all ticks every option matching the search (all options when the search is empty), keeping current ticks.
    const selectAll = () => onChange(options.filter(o => selected.includes(o.value) || visible(o)).map(o => o.value));
    return (
        <div className="picker" ref={root}>
            <span className="picker-label">{label}</span>
            <button type="button" className="picker-trigger" ref={trigger} aria-expanded={String(open)}
                aria-label={label + ': ' + (names.join(', ') || summary)} onClick={toggle}>
                {summary}
            </button>
            <div className="picker-panel" hidden={!open} role="group" aria-label={label + ' options'}>
                <input type="search" className="picker-search" ref={search} placeholder={placeholder}
                    aria-label={placeholder} value={query} onChange={e => setQuery(e.target.value)} />
                <div className="picker-list">
                    {options.map(option => {
                        const checked = selected.includes(option.value);
                        const className = 'picker-option' + (multi ? '' : ' picker-single') + (checked ? ' selected' : '');
                        return (
                            <button type="button" key={option.value} className={className} data-value={option.value}
                                role={multi ? 'checkbox' : 'radio'} aria-checked={String(checked)}
                                hidden={!visible(option)} onClick={() => pick(option.value)}>
                                <span className="picker-mark" aria-hidden="true">{checked ? '✓' : ''}</span>
                                <span>{option.text}</span>
                            </button>
                        );
                    })}
                </div>
                {multi ? (
                    <div className="picker-footer">
                        <button type="button" className="picker-all" onClick={selectAll}>Select all</button>
                        <button type="button" className="picker-clear" onClick={() => onChange([])}>Clear</button>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
